import { Close } from '@mui/icons-material';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { Button, ModalContentContainer, RadioBoxButton } from '@/components';
import {
  AllOption,
  ComplianceTypes,
  EpisodeFilterDefaults,
  SortByOptionsNasba,
  SortByOptionsNonNasba,
} from '@/constants';
import {
  selectUserCompliance,
  useAppSelector,
  useFetchEpisodeFacetsQuery,
} from '@/store';
import { getFacetOptions } from '@/utils';

export type ActiveFilter = 'sortBy' | 'mediaType' | 'audienceFunction' | null;

interface ModalSettings {
  title: string;
  options?: Record<string, { key: string; text: string }>;
  default?: string;
  multiple?: boolean;
}

interface FacetModalProps {
  activeFilter: ActiveFilter;
  currentValue: string[];
  onChange: (value: string[]) => void;
  onClose: () => void;
}

export const FacetModal = ({
  activeFilter,
  currentValue,
  onChange,
  onClose,
}: FacetModalProps) => {
  const { data: facets } = useFetchEpisodeFacetsQuery();
  const mediaTypeFacet = facets?.entities['media_type'];

  const [selectedValue, setSelectedValue] = useState<string[]>(currentValue);
  useEffect(() => {
    setSelectedValue(currentValue);
  }, [currentValue]);

  const compliance = useAppSelector(selectUserCompliance);
  const isCPE = compliance === ComplianceTypes.CPE;

  // Compose list of modal settings for the active filter
  const modalSettings: ModalSettings | null = useMemo(() => {
    switch (activeFilter) {
      case 'sortBy':
        return {
          title: 'Sort By',
          options: isCPE ? SortByOptionsNasba : SortByOptionsNonNasba,
          default: EpisodeFilterDefaults.sortBy,
        };
      case 'mediaType':
        return {
          title: `${mediaTypeFacet?.name || 'Media Type'}`,
          options: {
            [AllOption.key]: AllOption,
            ...getFacetOptions(mediaTypeFacet),
          },
          default: EpisodeFilterDefaults.mediaType,
        };
      case 'audienceFunction':
        return {
          title: 'Audience Function',
          options: {
            ...getFacetOptions(facets?.entities['audience_function']),
          },
          default: EpisodeFilterDefaults.audienceFunction,
        };
      default:
        return null;
    }
  }, [activeFilter, mediaTypeFacet, isCPE, facets?.entities]);

  return (
    <Drawer open={!!modalSettings} onClose={onClose} anchor="bottom">
      <ModalContentContainer stretch>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h1">
            {modalSettings ? modalSettings.title : ''}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close modal">
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {activeFilter && modalSettings?.options
            ? Object.entries(modalSettings.options).map(([key, { text }]) => (
                <RadioBoxButton
                  key={key}
                  label={text}
                  checked={selectedValue.includes(key)}
                  onChange={() => {
                    if (modalSettings.multiple) {
                      setSelectedValue(prev =>
                        prev.includes(key)
                          ? prev.filter(item => item !== key)
                          : [...prev, key],
                      );
                    } else {
                      setSelectedValue([key]);
                    }
                  }}
                />
              ))
            : null}
        </Box>
        <Box sx={{ mt: 4, display: 'flex', gap: 1 }}>
          <Button
            label="Reset"
            variant="secondary"
            onClick={() => {
              onChange([modalSettings?.default || '']);
              onClose();
            }}
          />
          <Button
            label="Save"
            onClick={() => {
              onChange(
                selectedValue.length
                  ? selectedValue
                  : [modalSettings?.default || ''],
              );
              onClose();
            }}
          />
        </Box>
      </ModalContentContainer>
    </Drawer>
  );
};
